import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface VolcesSeededitMultiIpParams {
  /**
   * 编辑提示词，必填，建议长度约300个字符，最大500个字符
   */
  prompt?: string;
  /**
   * Base64编码的图像数据数组，支持1-5张图像
   */
  binary_data_base64?: string[];
  /**
   * 图像URL数组，支持1-5张图像
   */
  image_urls?: string[];
  /**
   * 随机种子，-1或正整数，默认值为-1
   * @default -1
   */
  seed?: number;
  /**
   * 是否使用改写器，默认值为true
   * @default true
   */
  use_rephraser?: boolean;
  /**
   * 输出图像宽度，范围512-2048，默认值为1328
   * @default 1328
   * @range 512 - 2048
   */
  width?: number;
  /**
   * 输出图像高度，范围512-2048，默认值为1328
   * @default 1328
   * @range 512 - 2048
   */
  height?: number;
}

export interface VolcesSeededitMultiIpResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * volces_seededit_multi_ip
 *
 * Volces SeedEdit Multi IP Generator
 * 
 * Multi IP image editing implementation for Volces SeedEdit Multi IP (火山引擎SeedEdit多IP).
 * API Documentation: https://www.volcengine.com/docs/85128/1798096
 *
 * @example
 * ```typescript
 * import { initSeacloud, volcesSeededitMultiIp } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await volcesSeededitMultiIp({
 *   // 参数见 VolcesSeededitMultiIpParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 VolcesSeededitMultiIpParams 接口定义
 * @returns 生成的资源列表
 */
export async function volcesSeededitMultiIp(
  params: VolcesSeededitMultiIpParams,
): Promise<VolcesSeededitMultiIpResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'volces_seededit_multi_ip',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: VolcesSeededitMultiIpResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
